import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { FileText, Scale, Calendar, CreditCard, UserX } from 'lucide-react';

export default function Terms() {
  const { t, i18n } = useTranslation();
  const isGerman = i18n.language === 'de';

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-8">
        <Link to="/" className="text-emerald-600 hover:text-emerald-700 mb-4 inline-block">
          ← {t('common.back')}
        </Link>
        <h1 className="text-4xl font-bold text-gray-900 mb-2">
          {isGerman ? 'Allgemeine Geschäftsbedingungen (AGB)' : 'Terms and Conditions'}
        </h1>
        <p className="text-gray-600">
          {isGerman 
            ? 'Nutzungsbedingungen für ivoliq.io' 
            : 'Terms of Use for ivoliq.io'}
        </p>
        <p className="text-sm text-gray-500 mt-2">
          {isGerman ? 'Stand' : 'Last Updated'}: Januar 2025
        </p>
      </div>

      <div className="space-y-6">
        {/* 1. Geltungsbereich */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Scale className="h-5 w-5 text-emerald-600" />
              {isGerman ? '1. Geltungsbereich' : '1. Scope'}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <p className="text-gray-700 text-sm">
              {isGerman 
                ? 'Diese Allgemeinen Geschäftsbedingungen (AGB) regeln die Nutzung der SaaS-Plattform ivoliq.io (nachfolgend "Plattform") durch Freiberufler und Selbstständige (nachfolgend "Nutzer").'
                : 'These Terms and Conditions govern the use of the SaaS platform ivoliq.io (hereinafter "Platform") by freelancers and self-employed individuals (hereinafter "User").'}
            </p>
            <p className="text-gray-700 text-sm">
              {isGerman
                ? 'Die Plattform wird betrieben von Wilhelm Wagner, kontaktierbar unter:'
                : 'The Platform is operated by Wilhelm Wagner, contact:'}
            </p>
            <ul className="list-disc list-inside text-gray-700 text-sm space-y-1 ml-4">
              <li>E-Mail: admin@ivoliq.io</li>
              <li>{isGerman ? 'Weitere Details siehe' : 'Further details see'} <Link to="/impressum" className="text-emerald-600 hover:underline">Impressum</Link></li>
            </ul>
          </CardContent>
        </Card>

        {/* 2. Vertragsgegenstand */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5 text-emerald-600" />
              {isGerman ? '2. Vertragsgegenstand' : '2. Subject of Contract'}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <p className="text-gray-700 text-sm">
              {isGerman
                ? 'ivoliq bietet eine cloudbasierte Software-as-a-Service (SaaS) Lösung für Freiberufler und Selbstständige zur Verwaltung von:'
                : 'ivoliq provides a cloud-based Software-as-a-Service (SaaS) solution for freelancers and self-employed individuals to manage:'}
            </p>
            <ul className="list-disc list-inside text-gray-700 text-sm space-y-1 ml-4">
              <li>{isGerman ? 'Zeiterfassung und Projektverwaltung' : 'Time tracking and project management'}</li>
              <li>{isGerman ? 'Kundenverwaltung' : 'Customer management'}</li>
              <li>{isGerman ? 'Rechnungserstellung' : 'Invoice creation'}</li>
              <li>{isGerman ? 'Einnahmen- und Ausgabenverwaltung' : 'Income and expense management'}</li>
              <li>{isGerman ? 'Steuerberechnung (Deutschland und Österreich)' : 'Tax calculation (Germany and Austria)'}</li>
            </ul>
            <p className="text-gray-700 text-sm">
              {isGerman
                ? 'Der Leistungsumfang richtet sich nach dem gewählten Abonnement-Plan.'
                : 'The scope of services depends on the selected subscription plan.'}
            </p>
          </CardContent>
        </Card>

        {/* 3. Vertragsschluss und Registrierung */}
        <Card>
          <CardHeader>
            <CardTitle>
              {isGerman ? '3. Vertragsschluss und Registrierung' : '3. Contract Formation and Registration'}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <p className="text-gray-700 text-sm">
              {isGerman
                ? 'Der Vertrag kommt durch Registrierung des Nutzers auf der Plattform zustande.'
                : 'The contract is formed upon user registration on the Platform.'}
            </p>
            <ul className="list-disc list-inside text-gray-700 text-sm space-y-1 ml-4">
              <li>{isGerman ? 'Registrierung via E-Mail/Passwort oder Google OAuth' : 'Registration via email/password or Google OAuth'}</li>
              <li>{isGerman ? 'Das Land wird automatisch bei Registrierung erkannt (DE/AT)' : 'Country is automatically detected during registration (DE/AT)'}</li>
              <li>{isGerman ? 'Nutzer müssen ihre Steuerresidenz bestätigen' : 'Users must confirm their tax residency'}</li>
              <li>{isGerman ? 'Nur volljährige Personen dürfen sich registrieren' : 'Only persons of legal age may register'}</li>
            </ul>
          </CardContent>
        </Card>

        {/* 4. Abonnement-Pläne und Preise */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CreditCard className="h-5 w-5 text-emerald-600" />
              {isGerman ? '4. Abonnement-Pläne und Preise' : '4. Subscription Plans and Prices'}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <h3 className="font-semibold text-sm mb-2">{isGerman ? '4.1 Verfügbare Pläne' : '4.1 Available Plans'}</h3>
              <ul className="list-disc list-inside text-gray-700 text-sm space-y-1 ml-4">
                <li><strong>Free:</strong> {isGerman ? '€0/Monat – 1 Kunde, 1 Projekt, keine Steuerberechnung' : '€0/month – 1 customer, 1 project, no tax calculation'}</li>
                <li><strong>Basic:</strong> {isGerman ? '€9/Monat – 2 Kunden, 4 Projekte, automatische Rechnungserstellung' : '€9/month – 2 customers, 4 projects, automatic invoicing'}</li>
                <li><strong>Pro:</strong> {isGerman ? '€19/Monat – Unbegrenzte Kunden und Projekte, Steuerberechnung inkludiert' : '€19/month – Unlimited customers and projects, tax calculation included'}</li>
              </ul>
              <p className="text-gray-700 text-sm mt-2">
                {isGerman
                  ? 'Alle Preise verstehen sich zzgl. der gesetzlichen Umsatzsteuer.'
                  : 'All prices are subject to applicable VAT.'}
              </p>
            </div>

            <div>
              <h3 className="font-semibold text-sm mb-2">{isGerman ? '4.2 Zahlungsbedingungen' : '4.2 Payment Terms'}</h3>
              <p className="text-gray-700 text-sm">
                {isGerman
                  ? 'Die Abrechnung erfolgt monatlich im Voraus über Stripe. Bei Zahlungsverzug behält sich ivoliq das Recht vor, den Zugang vorübergehend zu sperren.'
                  : 'Billing is monthly in advance via Stripe. In case of payment delay, ivoliq reserves the right to temporarily suspend access.'}
              </p>
            </div>

            <div>
              <h3 className="font-semibold text-sm mb-2">{isGerman ? '4.3 Upgrade und Downgrade' : '4.3 Upgrade and Downgrade'}</h3>
              <ul className="list-disc list-inside text-gray-700 text-sm space-y-1 ml-4">
                <li>{isGerman ? 'Upgrades sind jederzeit möglich und werden sofort wirksam' : 'Upgrades are possible at any time and take effect immediately'}</li>
                <li>{isGerman ? 'Bei Downgrades: Kündigung zum Monatsende erforderlich, danach automatische Umstellung auf niedrigeren Plan' : 'For downgrades: Cancellation required by end of month, then automatic switch to lower plan'}</li>
                <li>{isGerman ? 'Bei Downgrade: Daten werden entsprechend der neuen Limits angepasst (älteste Einträge bleiben bestehen, neuere können gelöscht werden müssen)' : 'During downgrade: Data is adjusted according to new limits (oldest entries remain, newer ones may need to be deleted)'}</li>
              </ul>
            </div>
          </CardContent>
        </Card>

        {/* 5. Vertragslaufzeit und Kündigung */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calendar className="h-5 w-5 text-emerald-600" />
              {isGerman ? '5. Vertragslaufzeit und Kündigung' : '5. Contract Duration and Termination'}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <h3 className="font-semibold text-sm mb-2">{isGerman ? '5.1 Laufzeit' : '5.1 Duration'}</h3>
              <p className="text-gray-700 text-sm">
                {isGerman
                  ? 'Der Vertrag läuft auf unbestimmte Zeit. Die Mindestlaufzeit beträgt einen Monat bei bezahlten Plänen (Basic/Pro).'
                  : 'The contract runs indefinitely. The minimum term is one month for paid plans (Basic/Pro).'}
              </p>
            </div>

            <div>
              <h3 className="font-semibold text-sm mb-2">{isGerman ? '5.2 Ordentliche Kündigung' : '5.2 Regular Termination'}</h3>
              <ul className="list-disc list-inside text-gray-700 text-sm space-y-1 ml-4">
                <li>{isGerman ? 'Kündigung ist zum Monatsende möglich' : 'Termination is possible at the end of the month'}</li>
                <li>{isGerman ? 'Kündigungsfrist: 7 Tage vor Monatsende' : 'Notice period: 7 days before end of month'}</li>
                <li>{isGerman ? 'Kündigung erfolgt über die Stripe-Abonnementverwaltung oder per E-Mail an: support@ivoliq.io' : 'Termination via Stripe subscription management or email to: support@ivoliq.io'}</li>
              </ul>
            </div>

            <div>
              <h3 className="font-semibold text-sm mb-2">{isGerman ? '5.3 Außerordentliche Kündigung' : '5.3 Extraordinary Termination'}</h3>
              <p className="text-gray-700 text-sm">
                {isGerman
                  ? 'Das Recht zur fristlosen Kündigung aus wichtigem Grund bleibt unberührt. Ein wichtiger Grund liegt insbesondere vor bei schwerwiegenden Verstößen gegen diese AGB oder geltendes Recht.'
                  : 'The right to immediate termination for good cause remains unaffected. Good cause exists particularly in case of serious violations of these Terms or applicable law.'}
              </p>
            </div>

            <div>
              <h3 className="font-semibold text-sm mb-2">{isGerman ? '5.4 Folgen der Kündigung' : '5.4 Consequences of Termination'}</h3>
              <p className="text-gray-700 text-sm">
                {isGerman
                  ? 'Nach Vertragsende wird das Konto deaktiviert. Nutzerdaten werden gemäß unserer Datenschutzerklärung behandelt und können für 30 Tage wiederhergestellt werden. Danach erfolgt die endgültige Löschung.'
                  : 'After contract termination, the account is deactivated. User data is handled according to our Privacy Policy and can be restored for 30 days. After that, final deletion occurs.'}
              </p>
            </div>
          </CardContent>
        </Card>

        {/* 6. Rückerstattungen */}
        <Card>
          <CardHeader>
            <CardTitle>
              {isGerman ? '6. Rückerstattungen' : '6. Refunds'}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div>
              <h3 className="font-semibold text-sm mb-2">{isGerman ? '6.1 Grundsatz' : '6.1 Principle'}</h3>
              <p className="text-gray-700 text-sm">
                {isGerman
                  ? 'Bereits gezahlte Abonnementgebühren werden grundsätzlich nicht erstattet, da die Leistung sofort erbracht wird (Zugang zur Plattform).'
                  : 'Subscription fees already paid are generally not refunded, as the service is provided immediately (platform access).'}
              </p>
            </div>

            <div>
              <h3 className="font-semibold text-sm mb-2">{isGerman ? '6.2 Ausnahmen' : '6.2 Exceptions'}</h3>
              <ul className="list-disc list-inside text-gray-700 text-sm space-y-1 ml-4">
                <li>{isGerman ? 'Technische Ausfälle > 48 Stunden: Anteilige Erstattung nach Ermessen' : 'Technical outages > 48 hours: Pro-rata refund at discretion'}</li>
                <li>{isGerman ? 'Doppelabbuchung oder fehlerhafte Zahlungen: Volle Rückerstattung' : 'Double charges or erroneous payments: Full refund'}</li>
                <li>{isGerman ? 'Kulanzregelung innerhalb von 14 Tagen nach erstmaligem Upgrade: Rückerstattung möglich bei begründeter Anfrage an support@ivoliq.io' : 'Goodwill policy within 14 days after first upgrade: Refund possible upon justified request to support@ivoliq.io'}</li>
              </ul>
            </div>
          </CardContent>
        </Card>

        {/* 7. Account-Löschung */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <UserX className="h-5 w-5 text-emerald-600" />
              {isGerman ? '7. Account-Löschung' : '7. Account Deletion'}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div>
              <h3 className="font-semibold text-sm mb-2">{isGerman ? '7.1 Löschung durch Nutzer' : '7.1 Deletion by User'}</h3>
              <p className="text-gray-700 text-sm">
                {isGerman
                  ? 'Nutzer können ihren Account jederzeit über die Einstellungen löschen. Hierzu ist eine Passwortbestätigung erforderlich.'
                  : 'Users can delete their account at any time via settings. Password confirmation is required.'}
              </p>
            </div>

            <div>
              <h3 className="font-semibold text-sm mb-2">{isGerman ? '7.2 Ablauf bei Löschung' : '7.2 Deletion Process'}</h3>
              <ul className="list-disc list-inside text-gray-700 text-sm space-y-1 ml-4">
                <li>{isGerman ? 'Automatische Kündigung des Stripe-Abonnements' : 'Automatic cancellation of Stripe subscription'}</li>
                <li>{isGerman ? 'Alle Nutzerdaten werden unwiderruflich gelöscht' : 'All user data is irrevocably deleted'}</li>
                <li>{isGerman ? 'Bestätigungs-E-Mail an die registrierte E-Mail-Adresse' : 'Confirmation email to registered email address'}</li>
                <li>{isGerman ? 'Keine Rückerstattung für laufenden Abrechnungszeitraum' : 'No refund for current billing period'}</li>
              </ul>
            </div>

            <div>
              <h3 className="font-semibold text-sm mb-2">{isGerman ? '7.3 Aufbewahrung aus rechtlichen Gründen' : '7.3 Retention for Legal Reasons'}</h3>
              <p className="text-gray-700 text-sm">
                {isGerman
                  ? 'Rechnungsdaten und steuerrelevante Informationen werden gemäß gesetzlicher Aufbewahrungsfristen (z.B. 7 Jahre nach § 147 AO) gespeichert.'
                  : 'Invoice data and tax-relevant information are stored according to legal retention periods (e.g., 7 years per § 147 AO).'}
              </p>
            </div>
          </CardContent>
        </Card>

        {/* 8. Nutzerpflichten */}
        <Card>
          <CardHeader>
            <CardTitle>
              {isGerman ? '8. Nutzerpflichten' : '8. User Obligations'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-gray-700 text-sm mb-3">
              {isGerman
                ? 'Der Nutzer verpflichtet sich:'
                : 'The User agrees to:'}
            </p>
            <ul className="list-disc list-inside text-gray-700 text-sm space-y-1 ml-4">
              <li>{isGerman ? 'Wahrheitsgemäße Angaben bei der Registrierung zu machen' : 'Provide accurate information during registration'}</li>
              <li>{isGerman ? 'Zugangsdaten vertraulich zu behandeln' : 'Keep access credentials confidential'}</li>
              <li>{isGerman ? 'Die Plattform nicht missbräuchlich zu verwenden' : 'Not misuse the Platform'}</li>
              <li>{isGerman ? 'Keine rechtswidrigen Inhalte hochzuladen' : 'Not upload unlawful content'}</li>
              <li>{isGerman ? 'Bei Standortwechsel die Steuerresidenz zu aktualisieren' : 'Update tax residency upon relocation'}</li>
            </ul>
          </CardContent>
        </Card>

        {/* 9. Haftung */}
        <Card>
          <CardHeader>
            <CardTitle>
              {isGerman ? '9. Haftung und Gewährleistung' : '9. Liability and Warranty'}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <h3 className="font-semibold text-sm mb-2">{isGerman ? '9.1 Haftungsbeschränkung' : '9.1 Limitation of Liability'}</h3>
              <p className="text-gray-700 text-sm">
                {isGerman
                  ? 'ivoliq haftet unbeschränkt für Vorsatz und grobe Fahrlässigkeit sowie bei Verletzung von Leben, Körper oder Gesundheit. Bei leichter Fahrlässigkeit haftet ivoliq nur bei Verletzung wesentlicher Vertragspflichten (Kardinalpflichten). In diesem Fall ist die Haftung auf den vorhersehbaren, typischerweise eintretenden Schaden begrenzt.'
                  : 'ivoliq is fully liable for intent and gross negligence as well as for injury to life, body or health. For slight negligence, ivoliq is only liable for breach of essential contractual obligations. In this case, liability is limited to foreseeable, typically occurring damage.'}
              </p>
            </div>

            <div>
              <h3 className="font-semibold text-sm mb-2">{isGerman ? '9.2 Keine Steuerberatung' : '9.2 No Tax Advice'}</h3>
              <p className="text-gray-700 text-sm">
                {isGerman
                  ? 'Die bereitgestellten Steuerberechnungen dienen lediglich als Orientierungshilfe und ersetzen keine professionelle Steuerberatung. Der Nutzer ist selbst für die Richtigkeit seiner Steuererklärungen verantwortlich.'
                  : 'The provided tax calculations serve only as guidance and do not replace professional tax advice. The User is solely responsible for the accuracy of tax returns.'}
              </p>
            </div>

            <div>
              <h3 className="font-semibold text-sm mb-2">{isGerman ? '9.3 Verfügbarkeit' : '9.3 Availability'}</h3>
              <p className="text-gray-700 text-sm">
                {isGerman
                  ? 'ivoliq strebt eine Verfügbarkeit von 99% an, kann diese jedoch nicht garantieren. Wartungsarbeiten werden nach Möglichkeit angekündigt.'
                  : 'ivoliq aims for 99% availability but cannot guarantee it. Maintenance work will be announced when possible.'}
              </p>
            </div>
          </CardContent>
        </Card>

        {/* 10. Datenschutz */}
        <Card>
          <CardHeader>
            <CardTitle>
              {isGerman ? '10. Datenschutz' : '10. Data Protection'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-gray-700 text-sm">
              {isGerman
                ? 'Die Verarbeitung personenbezogener Daten erfolgt gemäß unserer'
                : 'The processing of personal data is carried out according to our'}{' '}
              <Link to="/privacy" className="text-emerald-600 hover:underline">
                {isGerman ? 'Datenschutzerklärung' : 'Privacy Policy'}
              </Link>
              {isGerman ? ', die Bestandteil dieser AGB ist.' : ', which is part of these Terms.'}
            </p>
          </CardContent>
        </Card>

        {/* 11. Änderungen der AGB */}
        <Card>
          <CardHeader>
            <CardTitle>
              {isGerman ? '11. Änderungen der AGB' : '11. Changes to Terms'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-gray-700 text-sm">
              {isGerman
                ? 'ivoliq behält sich das Recht vor, diese AGB zu ändern. Nutzer werden mindestens 30 Tage vor Inkrafttreten der Änderungen per E-Mail informiert. Widerspricht der Nutzer nicht innerhalb von 14 Tagen nach Zugang der Änderungsmitteilung, gelten die neuen AGB als akzeptiert. Im Falle eines Widerspruchs kann ivoliq das Vertragsverhältnis außerordentlich kündigen.'
                : 'ivoliq reserves the right to modify these Terms. Users will be informed at least 30 days before the changes take effect via email. If the User does not object within 14 days after receipt of the change notification, the new Terms are deemed accepted. In case of objection, ivoliq may terminate the contract extraordinarily.'}
            </p>
          </CardContent>
        </Card>

        {/* 12. Schlussbestimmungen */}
        <Card>
          <CardHeader>
            <CardTitle>
              {isGerman ? '12. Schlussbestimmungen' : '12. Final Provisions'}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div>
              <h3 className="font-semibold text-sm mb-2">{isGerman ? '12.1 Anwendbares Recht' : '12.1 Applicable Law'}</h3>
              <p className="text-gray-700 text-sm">
                {isGerman
                  ? 'Es gilt das Recht der Bundesrepublik Deutschland unter Ausschluss des UN-Kaufrechts.'
                  : 'The law of the Federal Republic of Germany applies, excluding the UN Convention on Contracts for the International Sale of Goods.'}
              </p>
            </div>

            <div>
              <h3 className="font-semibold text-sm mb-2">{isGerman ? '12.2 Gerichtsstand' : '12.2 Jurisdiction'}</h3>
              <p className="text-gray-700 text-sm">
                {isGerman
                  ? 'Gerichtsstand für alle Streitigkeiten ist der Sitz des Betreibers, sofern der Nutzer Kaufmann, juristische Person des öffentlichen Rechts oder öffentlich-rechtliches Sondervermögen ist.'
                  : 'The place of jurisdiction for all disputes is the operator\'s registered office, provided the User is a merchant, legal entity under public law or public special fund.'}
              </p>
            </div>

            <div>
              <h3 className="font-semibold text-sm mb-2">{isGerman ? '12.3 Salvatorische Klausel' : '12.3 Severability Clause'}</h3>
              <p className="text-gray-700 text-sm">
                {isGerman
                  ? 'Sollten einzelne Bestimmungen dieser AGB unwirksam sein oder werden, bleibt die Wirksamkeit der übrigen Bestimmungen unberührt.'
                  : 'Should individual provisions of these Terms be or become invalid, the validity of the remaining provisions remains unaffected.'}
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Kontakt */}
        <Card>
          <CardHeader>
            <CardTitle>
              {isGerman ? 'Kontakt bei Fragen' : 'Contact for Questions'}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <p className="text-gray-700 text-sm">
              {isGerman
                ? 'Bei Fragen zu diesen AGB wenden Sie sich bitte an:'
                : 'For questions regarding these Terms, please contact:'}
            </p>
            <ul className="text-gray-700 text-sm space-y-1">
              <li><strong>{isGerman ? 'Allgemeine Anfragen' : 'General Inquiries'}:</strong> info@ivoliq.io</li>
              <li><strong>Support:</strong> support@ivoliq.io</li>
              <li><strong>{isGerman ? 'Administration' : 'Administration'}:</strong> admin@ivoliq.io</li>
            </ul>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
